import AsyncStorage from '@react-native-async-storage/async-storage';
import type { JsonObject } from '@hexia/survey-client/core';
import type { GetSurveyFromMissionAssignationViewModel, SurveyFormModel } from '../../../api/api.g';

type DraftSnapshot = {
  data: JsonObject;
  currentPageIndex: number;
};

type DraftSession = {
  schemaVersion: 1;
  activeFormVersionId: string;
  forms: Record<string, DraftSnapshot>;
};

const keyOf = (missionAssignationId: string) => `hexia:survey-drafts:${missionAssignationId}`;
const writeQueues = new Map<string, Promise<void>>();

const parseObject = (value?: string): Record<string, unknown> => {
  try {
    const parsed = JSON.parse(value ?? '{}');
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
};

const readSession = async (key: string): Promise<DraftSession | undefined> => {
  try {
    const parsed = JSON.parse(await AsyncStorage.getItem(key) ?? 'null') as Partial<DraftSession> | null;
    if (parsed?.schemaVersion !== 1 || typeof parsed.activeFormVersionId !== 'string' || !parsed.forms || typeof parsed.forms !== 'object') return undefined;
    return parsed as DraftSession;
  } catch {
    return undefined;
  }
};

const enqueueWrite = (key: string, action: () => Promise<void>): Promise<void> => {
  const previous = writeQueues.get(key) ?? Promise.resolve();
  const next = previous.catch(() => undefined).then(action);
  writeQueues.set(key, next);
  void next.finally(() => {
    if (writeQueues.get(key) === next) writeQueues.delete(key);
  }).catch(() => undefined);
  return next;
};

const formsOf = (first?: SurveyFormModel): SurveyFormModel[] => {
  const forms: SurveyFormModel[] = [];
  let current = first;
  while (current?.formVersionId) {
    forms.push(current);
    current = current.nextForm;
  }
  return forms;
};

export const saveSurveyDraftSnapshot = (
  missionAssignationId: string,
  formVersionId: string,
  snapshot: DraftSnapshot,
): Promise<void> => {
  const key = keyOf(missionAssignationId);
  return enqueueWrite(key, async () => {
    const current = await readSession(key);
    const next: DraftSession = {
      schemaVersion: 1,
      activeFormVersionId: formVersionId,
      forms: {
        ...current?.forms,
        [formVersionId]: snapshot,
      },
    };
    await AsyncStorage.setItem(key, JSON.stringify(next));
  });
};

export const restoreSurveyDrafts = async (
  missionAssignationId: string,
  survey: GetSurveyFromMissionAssignationViewModel,
): Promise<void> => {
  const key = keyOf(missionAssignationId);
  await writeQueues.get(key)?.catch(() => undefined);
  const cached = await readSession(key);
  if (!cached) return;

  const forms = formsOf(survey.survey);
  for (const form of forms) {
    if (!form.formVersionId) continue;
    const snapshot = cached.forms[form.formVersionId];
    if (!snapshot || !snapshot.data || typeof snapshot.currentPageIndex !== 'number') continue;
    form.formAnswerJson = JSON.stringify(snapshot.data);
    form.metadataJson = JSON.stringify({
      ...parseObject(form.metadataJson),
      currentPageNo: snapshot.currentPageIndex,
    });
  }

  const first = forms[0];
  if (first && cached.forms[cached.activeFormVersionId]) {
    first.metadataJson = JSON.stringify({
      ...parseObject(first.metadataJson),
      currentMission: cached.activeFormVersionId,
    });
  }
};

export const clearSurveyDrafts = (missionAssignationId: string): Promise<void> => {
  const key = keyOf(missionAssignationId);
  return enqueueWrite(key, () => AsyncStorage.removeItem(key));
};
